// App.js (Fully Updated, Error-Free Version)

import React, { useState, createContext, useContext, useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
  Navigate,
  Link
} from 'react-router-dom';
import {
  Home,
  Book,
  ChevronLast,
  ChevronFirst,
  LogOut,
  User,
  Video,
  Settings as SettingsIcon,
  ChevronUp,
  X,
  MessageSquare
} from 'lucide-react';
import ClassForm from './components/ClassForm';
import ClassList from './components/ClassList';
import ClassDetail from './components/ClassDetail';
import Dashboard from './components/Dashboard';
import LectureList from './components/LectureList';
import CanvasCallback from './components/CanvasCallback';
import LectureDetail from './components/LectureDetail';
import LectureChat from './pages/LectureChat';
import LectureAI from './components/LectureAI';
import { Toaster } from 'react-hot-toast';
import {
  ClerkProvider,
  SignIn,
  SignUp,
  SignedIn,
  SignedOut,
  useUser,
  useClerk
} from '@clerk/clerk-react';
import ProtectedRoute from './components/ProtectedRoute';
import AuthLayout from './components/AuthLayout';
import { ErrorBoundaryWithNavigation } from './components/ErrorBoundary';
import Profile from './components/Profile';
import Onboarding from './components/Onboarding';
import { useAuthenticatedFetch } from './utils/api';
import MeetingList from './components/MeetingList';
import MeetingRecorder from './components/MeetingRecorder';
import MeetingDetail from './components/MeetingDetail';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import './styles/global.css';
import Settings from './components/Settings';
import SharedMeetingView from './components/SharedMeetingView';
import SharedClassView from './components/SharedClassView';
import SharedLectureView from './components/SharedLectureView';
import AdminPanel from './components/AdminPanel';
import ChatHub from './pages/ChatHub';
import TestLectureProcessor from './components/TestLectureProcessor';
import NotFound from './components/NotFound';

// --------------------------------------------------------------------------------
// CONTEXT
// --------------------------------------------------------------------------------
const SidebarContext = createContext();
const MobileNavContext = createContext();

// --------------------------------------------------------------------------------
// SIDEBAR
// --------------------------------------------------------------------------------
const Sidebar = ({ children, logout }) => {
  const { theme, themes } = useTheme();
  const location = useLocation();
  const [expanded, setExpanded] = useState(location.pathname === '/');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    setExpanded(location.pathname === '/');
  }, [location.pathname]);

  // Add window resize handler for mobile detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile && expanded) {
        setExpanded(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [expanded]);

  const navigate = useNavigate();
  const { user } = useUser();

  if (!user) return null;

  // Find if the current theme belongs to the dark category
  const isDarkTheme = Object.values(themes.dark).some(darkTheme => darkTheme.name === theme?.name);

  return (
    <nav
      className={`
        h-full flex-1 flex border-2 flex-col 
        bg-theme text-theme border-theme
        rounded-[20px] transition-all duration-300
        ${isMobile ? 'w-64' : expanded ? 'w-64' : 'w-[4.5rem]'}
      `}
    >
      <div className="p-4 pb-2 flex justify-between items-center">
        <img
          src={
            isDarkTheme
              ? '/classify-logo-dark.png'
              : '/classify-logo-light.png'
          }
          className={`overflow-hidden transition-all ${isMobile || expanded ? 'w-20' : 'w-0'}`}
          alt="Classify"
        />
        {!isMobile && (
          <button
            onClick={() => setExpanded(curr => !curr)}
            className="p-1.5 rounded-lg"
          >
            {expanded ? <ChevronFirst /> : <ChevronLast />}
          </button>
        )}
      </div>

      <SidebarContext.Provider value={{ expanded: isMobile ? true : expanded, isMobile }}>
        <ul className="flex-1 px-3">{children}</ul>
      </SidebarContext.Provider>

      <div className="p-3">
        <div className="w-full h-px bg-accent mb-3"></div>
        
        <div
          className={`
            flex items-center cursor-pointer
            ${isMobile || expanded ? 'w-full' : 'justify-center w-full'}
          `}
          onClick={() => navigate('/settings')}
        >
          <img
            src={user?.imageUrl}
            alt=""
            className="w-10 h-10 rounded-lg"
          />
          {(isMobile || expanded) && (
            <div className="ml-3">
              <h4 className="font-semibold text-theme">{user?.firstName}</h4>
              <span className="text-xs text-accent">
                {user?.primaryEmailAddress?.emailAddress}
              </span>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

// --------------------------------------------------------------------------------
// SIDEBAR ITEM
// --------------------------------------------------------------------------------
const SidebarItem = ({ icon, text, active, to, onClick }) => {
  const { expanded, isMobile } = useContext(SidebarContext);
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(to);
    }
  };

  return (
    <li
      className={`
        relative flex items-center py-2 px-3 my-1
        font-medium rounded-lg cursor-pointer
        transition-colors group
        ${isMobile ? 'h-12' : 'h-11'} // Taller touch targets on mobile
        ${active
          ? 'bg-primary text-theme'
          : 'hover:bg-secondary text-accent'
        }
      `}
      onClick={handleClick}
    >
      <div className="flex items-center justify-center">
        {icon}
      </div>
      <span className={`overflow-hidden transition-all ${expanded ? 'w-52 ml-3' : 'w-0'}`}>
        {text}
      </span>
    </li>
  );
};

// --------------------------------------------------------------------------------
// MOBILE BOTTOM NAVIGATION
// --------------------------------------------------------------------------------
const MobileNavigation = ({ items, user, classes, toggleClassesMenu }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const { theme } = useTheme();
  const longPressTimeoutRef = useRef(null);
  const [isStandalone, setIsStandalone] = useState(false);
  
  // Check if the app is running in standalone/PWA mode
  useEffect(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.matchMedia('(display-mode: fullscreen)').matches || 
                  (window.navigator.standalone === true);
    setIsStandalone(isPWA);
  }, []);
  
  const getActiveStatus = (route) => {
    if (route === '/') {
      return pathname === '/';
    }
    if (route === '/classes') {
      return pathname.includes('/class/') || pathname === '/classes';
    }
    return pathname.includes(route);
  };

  const handleTouchStart = (item) => {
    if (item.name === 'Classes' && item.subItems?.length > 0) {
      longPressTimeoutRef.current = setTimeout(() => {
        toggleClassesMenu();
      }, 500); // 500ms long press to open menu
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current);
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-theme border-t border-theme z-50 shadow-lg" 
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        paddingBottom: 'env(safe-area-inset-bottom)',
        boxSizing: 'border-box',
        backgroundColor: theme.background || '#ffffff',
        marginBottom: 0,
        zIndex: 9999,
        borderBottom: 'none',
        ...(isStandalone ? {
          bottom: 0,
          position: 'fixed',
          width: '100%'
        } : {})
      }}
    >
      <div className="flex justify-around items-center h-16">
        {items.map((item) => {
          const isActive = getActiveStatus(item.route);
          return (
            <button
              key={item.name}
              className={`flex flex-col items-center justify-center w-1/4 py-1`}
              onClick={() => {
                // Always navigate directly to the item's route
                navigate(item.route);
              }}
              onTouchStart={() => handleTouchStart(item)}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
            >
              <div className={`p-1.5 rounded-full ${isActive ? 'bg-primary' : ''} mb-1`}>
                <span style={{ color: isActive ? theme.text : theme.accent }}>
                  {item.icon}
                </span>
              </div>
              <span className={`text-xs font-medium ${isActive ? 'text-theme font-bold' : 'text-accent'}`}>
                {item.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------------
// MOBILE CLASSES MENU
// --------------------------------------------------------------------------------
const MobileClassesMenu = ({ classes, isOpen, closeMenu }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 animate-fade-in"
        onClick={closeMenu}
      />
      
      {/* Menu Panel */}
      <div className="fixed inset-x-0 bottom-0 z-50 bg-theme rounded-t-2xl max-h-[80vh] flex flex-col animate-slide-up shadow-xl"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom)',
          boxSizing: 'border-box',
          backgroundColor: theme.background || '#ffffff'
        }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-theme">
          <h2 className="text-lg font-bold text-theme">Your Classes</h2>
          <button onClick={closeMenu} className="p-1 rounded-full hover:bg-secondary">
            <X size={24} className="text-theme" />
          </button>
        </div>
        
        {/* Drag indicator */}
        <div className="w-12 h-1.5 bg-accent opacity-30 rounded-full mx-auto my-2"></div>
        
        <div className="flex-1 overflow-auto p-4">
          {classes && classes.length > 0 ? (
            <div className="space-y-3">
              {classes.map((cls) => (
                <div
                  key={cls.id}
                  className="p-4 bg-secondary rounded-xl shadow-sm transition-transform active:scale-97 hover:bg-secondary-hover"
                  onClick={() => {
                    navigate(`/class/${cls.id}/lectures`);
                    closeMenu();
                  }}
                >
                  <h3 className="font-medium text-theme mb-1">{cls.title}</h3>
                  {cls.course_code && (
                    <p className="text-sm text-accent">{cls.course_code}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-accent">
              <Book size={48} className="mx-auto mb-4 opacity-40" />
              <p>No classes added yet</p>
              <p className="text-sm mt-2 mb-6">Add classes to view them here</p>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-theme">
          <button
            onClick={() => {
              navigate('/classes');
              closeMenu();
            }}
            className="w-full py-3 bg-primary text-theme rounded-lg font-medium transition-colors hover:bg-primary-hover"
          >
            Manage All Classes
          </button>
          <button
            onClick={() => {
              navigate('/add');
              closeMenu();
            }}
            className="w-full py-3 mt-2 border border-primary text-primary rounded-lg font-medium transition-colors hover:bg-secondary"
          >
            Add New Class
          </button>
        </div>
      </div>
    </>
  );
};

// --------------------------------------------------------------------------------
// MAIN APP CONTENT
// --------------------------------------------------------------------------------
const AppContent = ({
  addClass,
  deleteClass,
  updateClass
}) => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, themes } = useTheme();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [classes, setClasses] = useState([]);
  const authenticatedFetch = useAuthenticatedFetch();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [classesMenuOpen, setClassesMenuOpen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  
  // Check if the app is running in standalone/PWA mode
  useEffect(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.matchMedia('(display-mode: fullscreen)').matches || 
                  (window.navigator.standalone === true);
    setIsStandalone(isPWA);
  }, []);
  
  // Find if the current theme belongs to the dark category
  const isDarkTheme = Object.values(themes.dark).some(darkTheme => darkTheme.name === theme?.name);

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when location changes (on mobile)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setClassesMenuOpen(false);
    }
  }, [location.pathname, isMobile]);

  const getCurrentSemesterInfo = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth();

    let semester;
    if (month >= 0 && month <= 4) {
      semester = 'Spring';
    } else if (month >= 5 && month <= 7) {
      semester = 'Summer';
    } else {
      semester = 'Fall';
    }

    return { semester, year };
  };

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const { semester, year } = getCurrentSemesterInfo();
        const queryParams = new URLSearchParams({
          semester,
          year
        }).toString();

        const response = await authenticatedFetch(
          `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/classes?${queryParams}`
        );
        const data = await response.json();
        setClasses(data);
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };

    fetchClasses();
  }, []);

  const logout = () => {
    signOut().then(() => {
      navigate('/sign-in');
    });
  };

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleClassesMenu = () => {
    setClassesMenuOpen(!classesMenuOpen);
  };

  const sidebarItems = [
    {
      name: 'Dashboard',
      icon: <Home size={isMobile ? 24 : 20} />,
      route: '/'
    },
    {
      name: 'Classes',
      icon: <Book size={isMobile ? 24 : 20} />,
      route: '/classes',
      subItems: classes.map(cls => ({
        name: cls.title,
        route: `/class/${cls.id}/lectures`
      }))
    },
    {
      name: 'Meetings',
      icon: <Video size={isMobile ? 24 : 20} />,
      route: '/meetings'
    },
    {
      name: 'Settings',
      icon: <SettingsIcon size={isMobile ? 24 : 20} />,
      route: '/settings'
    },
    {
      name: 'Chat',
      icon: <MessageSquare size={isMobile ? 24 : 20} />,
      route: '/chat'
    }
  ];

  return (
    <div className="fixed inset-0 flex bg-theme theme-transition" style={{
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      margin: 0,
      padding: 0,
      ...(isStandalone ? {
        // Apply specific styles for PWA mode
        height: '-webkit-fill-available',
        maxHeight: '-webkit-fill-available'
      } : {})
    }}>
      {/* Mobile Header */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 z-40 bg-theme border-b border-theme" style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)'
        }}>
          <div className="flex items-center justify-between px-4 h-14">
            <img
              src={isDarkTheme ? '/classify-logo-dark.png' : '/classify-logo-light.png'}
              className="h-8"
              alt="Classify"
            />
            <button
              onClick={() => navigate('/settings')}
              className="w-8 h-8 rounded-full overflow-hidden"
            >
              <img
                src={user?.imageUrl}
                alt={user?.firstName || "User"}
                className="w-full h-full object-cover"
              />
            </button>
          </div>
        </div>
      )}
      
      {/* Desktop Sidebar */}
      {!isMobile && (
        <div className="relative p-2.5">
          <Sidebar logout={logout}>
            {sidebarItems.map(item => (
              <SubMenuWrapper
                key={item.name}
                item={item}
                hoveredItem={hoveredItem}
                setHoveredItem={setHoveredItem}
                closeSidebar={closeSidebar}
                isMobile={isMobile}
              />
            ))}
          </Sidebar>
        </div>
      )}

      {/* Main content */}
      <div 
        className={`flex-1 overflow-auto bg-theme ${isMobile ? 'w-full' : ''}`}
        style={{
          paddingTop: isMobile ? 'calc(3.5rem + env(safe-area-inset-top))' : '0',
          // Handle padding differently for PWA mode
          paddingBottom: isStandalone ? '0' : (isMobile ? 'calc(4rem)' : '0'),
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          ...(isMobile ? {
            position: 'absolute',
            top: 0,
            left: 0, 
            right: 0,
            bottom: 0,
            height: '100%',
            overscrollBehavior: 'none'
          } : {}),
          // Apply specific styles for PWA mode
          ...(isStandalone ? {
            bottom: isMobile ? '4rem' : '0',
            marginBottom: '0',
            height: isMobile ? 'calc(100% - 4rem)' : '100%'
          } : {}),
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard classes={classes} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meetings"
            element={
              <ProtectedRoute>
                <MeetingList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meetings/new"
            element={
              <ProtectedRoute>
                <MeetingRecorder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meetings/:meetingId"
            element={
              <ProtectedRoute>
                <MeetingDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meetings/share/:shareToken"
            element={
              <ProtectedRoute>
                <MeetingDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/classes/share/:shareToken"
            element={
              <ProtectedRoute>
                <SharedClassView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/add"
            element={<ClassForm addClass={addClass} />}
          />
          <Route
            path="/classes"
            element={
              <ProtectedRoute>
                <ClassList
                  classes={classes}
                  deleteClass={deleteClass}
                  addClass={addClass}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/class/:id/lectures"
            element={
              <ProtectedRoute>
                <LectureList classes={classes} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/class/:id/lecture/:lectureId"
            element={
              <ProtectedRoute>
                <LectureDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lectures/share/:shareToken"
            element={
              <ProtectedRoute>
                <SharedLectureView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/class/:id/new-lecture"
            element={
              <ProtectedRoute>
                <ClassDetail classes={classes} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit/:id"
            element={
              <ClassForm
                classes={classes}
                updateClass={updateClass}
              />
            }
          />
          <Route
            path="/profile"
            element={<Profile />}
          />
          <Route
            path="/canvas-callback"
            element={<CanvasCallback addClass={addClass} />}
          />
          <Route
            path="/class/:classId/lecture/:lectureId/chat"
            element={
              <ProtectedRoute>
                <LectureChat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/class/:id/lecture/:lectureId/ai"
            element={
              <ProtectedRoute>
                <LectureAI />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route path="/admin" element={
            <SignedIn>
              <AdminPanel />
            </SignedIn>
          } />
          <Route path="/chat" element={
            <ProtectedRoute>
              <ChatHub />
            </ProtectedRoute>
          } />
          <Route path="/chat/new" element={
            <ProtectedRoute>
              <LectureChat />
            </ProtectedRoute>
          } />
          <Route path="/chat/:chatId" element={
            <ProtectedRoute>
              <LectureChat />
            </ProtectedRoute>
          } />
          <Route path="/test-lecture-processor" element={
            <ProtectedRoute>
              <TestLectureProcessor />
            </ProtectedRoute>
          } />
          
          {/* 404 Not Found - This should be the last route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      
      {/* Mobile Navigation */}
      {isMobile && (
        <>
          <MobileNavigation 
            items={sidebarItems}
            user={user}
            classes={classes}
            toggleClassesMenu={toggleClassesMenu}
          />
          <MobileClassesMenu 
            classes={classes}
            isOpen={classesMenuOpen}
            closeMenu={() => setClassesMenuOpen(false)}
          />
        </>
      )}
      
      <Toaster 
        position={isMobile ? "bottom-center" : "bottom-right"} 
        toastOptions={{
          duration: isMobile ? 3000 : 5000,
          style: {
            maxWidth: isMobile ? '90vw' : '350px',
            marginBottom: isMobile ? '80px' : '20px', // Added margin to avoid overlap with bottom nav
          }
        }}
      />
    </div>
  );
};

// --------------------------------------------------------------------------------
// SUB-MENU WRAPPER
// --------------------------------------------------------------------------------
function SubMenuWrapper({ item, hoveredItem, setHoveredItem, closeSidebar, isMobile }) {
  const navigate = useNavigate();
  const { expanded } = useContext(SidebarContext);
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const isClassesMenu = item.name === 'Classes';

  const handleMouseEnter = () => {
    if (!isMobile) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      setIsLeaving(false);
      setIsHovered(true);
      setHoveredItem(item.name);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      if (!expanded) {
        setIsLeaving(true);
        hoverTimeoutRef.current = setTimeout(() => {
          setIsHovered(false);
          setHoveredItem(null);
          setIsLeaving(false);
        }, 200); // Match this with animation duration
      } else {
        setIsHovered(false);
        setHoveredItem(null);
      }
    }
  };

  const handleItemClick = () => {
    // On mobile, always navigate directly to the main route
    if (isMobile) {
      navigate(item.route);
      if (closeSidebar) closeSidebar();
    } else {
      // On desktop, handle submenus for Classes
      if (isClassesMenu && !expanded) {
        setIsExpanded(!isExpanded);
      } else {
        navigate(item.route);
      }
    }
  };

  const handleSubItemClick = (route) => {
    navigate(route);
    if (isMobile && closeSidebar) {
      closeSidebar();
    }
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative"
    >
      {/* Main menu item */}
      <SidebarItem
        icon={item.icon}
        text={item.name}
        active={window.location.pathname === item.route || (isClassesMenu && window.location.pathname.includes('/class/'))}
        to={item.route}
        onClick={handleItemClick}
      />

      {/* Desktop submenu */}
      {!isMobile && item.subItems && isClassesMenu && (
        <>
          {expanded ? (
            <div 
              className={`
                pl-6 border-l border-theme overflow-hidden
                transition-all duration-300 ease-in-out
                ${isHovered ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}
              `}
            >
              {item.subItems.map(subItem => (
                <div
                  key={subItem.route}
                  onClick={() => navigate(subItem.route)}
                  className={`
                    py-2 px-3 my-0.5 cursor-pointer rounded-md
                    transition-all duration-200 ease-in-out
                    transform hover:translate-x-1
                    bg-theme hover:bg-primary text-theme
                  `}
                >
                  {subItem.name}
                </div>
              ))}
              {item.subItems.length === 0 && (
                <div className="px-4 py-2 text-accent text-sm italic">
                  No classes added yet
                </div>
              )}
            </div>
          ) : isHovered ? (
            <div
              onMouseEnter={() => {
                if (hoverTimeoutRef.current) {
                  clearTimeout(hoverTimeoutRef.current);
                }
                setIsLeaving(false);
                setIsHovered(true);
              }}
              onMouseLeave={handleMouseLeave}
              className={`
                absolute left-full ml-6 top-0
                rounded-lg shadow-lg min-w-[200px] z-[9999]
                border border-theme bg-theme
              `}
              style={{
                animation: `${isLeaving ? 'fadeOut' : 'fadeIn'} 0.2s ease-in-out`,
                opacity: isLeaving ? 0 : 1
              }}
            >
              {item.subItems.map(subItem => (
                <div
                  key={subItem.route}
                  onClick={() => handleSubItemClick(subItem.route)}
                  className={`
                    px-4 py-2.5 cursor-pointer first:rounded-t-lg last:rounded-b-lg
                    transition-all duration-200 ease-in-out
                    hover:bg-primary text-theme
                  `}
                >
                  {subItem.name}
                </div>
              ))}
              {item.subItems.length === 0 && (
                <div className="px-4 py-2.5 text-accent text-sm italic">
                  No classes added yet
                </div>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------------
// CLERK PROVIDER WRAPPER
// --------------------------------------------------------------------------------
const ClerkProviderWithRoutes = ({
  classes,
  setClasses,
  addClass,
  deleteClass,
  updateClass
}) => {
  const navigate = useNavigate();
  const rootUrl = 'https://learnwithclassify.com';

  return (
    <ClerkProvider
      publishableKey={process.env.REACT_APP_CLERK_PUBLISHABLE_KEY}
      navigate={to => navigate(to)}
      afterSignInUrl="/"
      signUpProps={{
        redirectUrl: `${rootUrl}/onboarding`,
        afterSignUpUrl: `${rootUrl}/onboarding`,
        routing: 'path',
        path: '/sign-up',
        signInUrl: '/sign-in'
      }}
      signInProps={{
        routing: 'path',
        path: '/sign-in',
        signUpUrl: '/sign-up',
        redirectUrl: rootUrl
      }}
    >
      <ErrorBoundaryWithNavigation>
        <div className="fixed inset-0 bg-theme theme-transition">
          <Routes>
            <Route
              path="/sign-in/*"
              element={
                <SignedOut>
                  <AuthLayout>
                    <SignIn
                      routing="path"
                      path="/sign-in"
                      signUpUrl="/sign-up"
                      afterSignInUrl="/"
                      redirectUrl="/"
                    />
                  </AuthLayout>
                </SignedOut>
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <SignedOut>
                  <AuthLayout>
                    <SignUp
                      routing="path"
                      path="/sign-up"
                      signInUrl="/sign-in"
                      afterSignUpUrl="/onboarding"
                      redirectUrl="/onboarding"
                    />
                  </AuthLayout>
                </SignedOut>
              }
            />
            <Route
              path="/onboarding"
              element={
                <SignedIn>
                  <Onboarding />
                </SignedIn>
              }
            />
            <Route
              path="/*"
              element={
                <>
                  <SignedIn>
                    <RequireProfile>
                      <AppContent
                        classes={classes}
                        addClass={addClass}
                        deleteClass={deleteClass}
                        updateClass={updateClass}
                      />
                    </RequireProfile>
                  </SignedIn>
                  <SignedOut>
                    <Navigate to="/sign-in" replace />
                  </SignedOut>
                </>
              }
            />
            
            {/* 404 route for auth pages */}
            <Route path="*" element={
              <SignedOut>
                <AuthLayout>
                  <NotFound />
                </AuthLayout>
              </SignedOut>
            } />
          </Routes>
        </div>
      </ErrorBoundaryWithNavigation>
    </ClerkProvider>
  );
};

// --------------------------------------------------------------------------------
// REQUIRE PROFILE
// --------------------------------------------------------------------------------
const RequireProfile = ({ children }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);

  useEffect(() => {
    const checkProfile = async () => {
      if (!user) return;
      try {
        const response = await fetch(`/api/users/profile/${user.id}`);
        setHasProfile(response.ok);
      } catch (error) {
        console.error('Failed to check profile:', error);
        setHasProfile(false);
      }
      setLoading(false);
    };
    checkProfile();
  }, [user]);

  if (loading) {
    return <div>Loading...</div>;
  }
  if (!hasProfile) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
};

// --------------------------------------------------------------------------------
// ROOT APP
// --------------------------------------------------------------------------------
export default function App() {
  const [classes, setClasses] = useState([]);

  const addClass = (newClass) => {
    setClasses(prev => [...prev, newClass]);
  };
  const deleteClass = (classId) => {
    setClasses(prev => prev.filter(c => c.id !== classId));
  };
  const updateClass = (updatedClass) => {
    setClasses(prev => prev.map(c => (c.id === updatedClass.id ? updatedClass : c)));
  };

  if (!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY) {
    throw new Error('Missing Publishable Key');
  }

  return (
    <Router>
      <ThemeProvider>
        <ClerkProviderWithRoutes
          classes={classes}
          setClasses={setClasses}
          addClass={addClass}
          deleteClass={deleteClass}
          updateClass={updateClass}
        />
      </ThemeProvider>
    </Router>
  );
}
